import { PageLink } from '../../../../../_metronic/layout/core'
import { EMatchingPayoutTicketStatus } from '../../../../../types/enums'
import {
  matchingCapabilities,
  MatchingFinishedPayoutFilterState,
  MatchingPayoutTicketListObject,
} from '../../core/_models'
import { getDefaultDateRange } from '../../../../../utils/getDefaultDateRange'

const defaultList: MatchingPayoutTicketListObject = {}
const perPage = 20

export const breadcrumbs: Array<PageLink> = [
  {
    title: 'Активные выплаты',
    path: '/matching/payout/active',
    isSeparator: false,
    isActive: false,
    roles: matchingCapabilities.canViewFinishedPayouts,
  },
  {
    title: 'Завершённые выплаты',
    path: '/matching/payout/finished',
    isSeparator: false,
    isActive: true,
    roles: matchingCapabilities.canViewFinishedPayouts,
  },
  {
    title: 'Отказы',
    path: '/matching/payout/refusals',
    isSeparator: false,
    isActive: false,
    roles: matchingCapabilities.canViewPayoutRefusals,
  },
]

// Статусы завершённых выплат, между которыми фильтрует пользователь.
// Пустой выбор трактуется как «оба» — список не может быть без статусов.
const relevantStatusList: Array<EMatchingPayoutTicketStatus> = [
  EMatchingPayoutTicketStatus.COMPLETED,
  EMatchingPayoutTicketStatus.DECLINED,
]

// Опции для фильтра статуса (лейблы — по ТЗ CPAYMENT-1638).
export const statusFilterOptions: Array<{ value: string; label: string }> = [
  { value: EMatchingPayoutTicketStatus.COMPLETED, label: 'Завершён' },
  { value: EMatchingPayoutTicketStatus.DECLINED, label: 'Отменён' },
]

const defaultDateRange = getDefaultDateRange()

const defaultFrom =
  defaultDateRange.startDate instanceof Date
    ? defaultDateRange.startDate
    : new Date(defaultDateRange.startDate)
const defaultTo =
  defaultDateRange.endDate instanceof Date
    ? defaultDateRange.endDate
    : new Date(defaultDateRange.endDate)

// Дефолтное состояние формы фильтров (оно же — цель кнопки «Сбросить»).
const defaultFilters: MatchingFinishedPayoutFilterState = {
  search: '',
  statusList: [EMatchingPayoutTicketStatus.COMPLETED, EMatchingPayoutTicketStatus.DECLINED],
  merchantTokenList: [],
  paymentMethodList: [],
  dateRange: { from: defaultFrom, to: defaultTo, mode: 'created' },
}

// Плоское представление фильтров в URL. Даты держим плоско (dateStart/dateEnd/completed),
// т.к. qs не восстанавливает Date внутри вложенного dateRange-объекта; массивы — key[]=…
export interface FinishedPayoutUrlShape {
  search?: string
  completed?: boolean
  dateStart?: Date
  dateEnd?: Date
  statusList?: string[]
  merchantTokenList?: string[]
  paymentMethodList?: string[]
}

const urlDefaults: FinishedPayoutUrlShape = {
  search: '',
  completed: false,
  dateStart: defaultFrom,
  dateEnd: defaultTo,
  statusList: [EMatchingPayoutTicketStatus.COMPLETED, EMatchingPayoutTicketStatus.DECLINED],
  merchantTokenList: [],
  paymentMethodList: [],
}

// Алиасы старых URL-ключей на плоские ключи (обратная совместимость ссылок).
const aliasFilters: Record<string, string> = {
  ticket_id: 'search',
  start: 'dateStart',
  end: 'dateEnd',
  createdAtStart: 'dateStart',
  createdAtEnd: 'dateEnd',
  completionDateStart: 'dateStart',
  completionDateEnd: 'dateEnd',
}

/** Плоское URL-представление → состояние формы. */
export const urlToFilterState = (
  url: FinishedPayoutUrlShape
): MatchingFinishedPayoutFilterState => ({
  search: url.search || '',
  statusList: url.statusList?.length ? url.statusList : [...relevantStatusList],
  merchantTokenList: url.merchantTokenList ?? [],
  paymentMethodList: url.paymentMethodList ?? [],
  dateRange: {
    from:
      url.dateStart instanceof Date
        ? url.dateStart
        : url.dateStart
        ? new Date(url.dateStart)
        : defaultFrom,
    to: url.dateEnd instanceof Date ? url.dateEnd : url.dateEnd ? new Date(url.dateEnd) : defaultTo,
    mode: url.completed ? 'completed' : 'created',
  },
})

/** Состояние формы → плоское URL-представление. */
export const filterStateToUrl = (
  state: MatchingFinishedPayoutFilterState
): FinishedPayoutUrlShape => ({
  search: state.search || '',
  completed: state.dateRange?.mode === 'completed',
  dateStart: state.dateRange?.from,
  dateEnd: state.dateRange?.to,
  statusList: state.statusList,
  merchantTokenList: state.merchantTokenList,
  paymentMethodList: state.paymentMethodList,
})

export const matchingFinishedPayoutsConfig = {
  breadcrumbs,
  defaultList,
  perPage,
  relevantStatusList,
  defaultFilters,
  urlDefaults,
  aliasFilters,
}
